import React, { memo } from "react"
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from "@heroicons/react/24/outline"

const Pagination = ({
  currentPage = 1,
  lastPage = 1,
  perPage = 15,
  total = 0,
  onPageChange,
  onPerPageChange,
  isLoading = false,
  showSizeSelector = true,
  sizeOptions = [5, 10, 15, 25, 50, 100],
  className = '',
}) => {
  const getVisiblePages = () => {
    const pages = []
    const maxVisible = 5

    if (lastPage <= maxVisible) {
      for (let i = 1; i <= lastPage; i++) {
        pages.push(i)
      }
    } else {
      const start = Math.max(1, currentPage - 2)
      const end = Math.min(lastPage, currentPage + 2)

      if (start > 1) {
        pages.push(1)
        if (start > 2) pages.push("...")
      }

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }

      if (end < lastPage) {
        if (end < lastPage - 1) pages.push("...")
        pages.push(lastPage)
      }
    }

    return pages
  }

  const handlePageChange = (page) => {
    if (page >= 1 && page <= lastPage && page !== currentPage && !isLoading) {
      onPageChange(page, perPage)
    }
  }

  const handleSizeChange = (newSize) => {
    if (!isLoading) {
      if (typeof onPerPageChange === "function") {
        onPerPageChange(newSize)
      } else if (typeof onPageChange === "function") {
        onPageChange(1, newSize)
      }
    }
  }

  const startItem = (currentPage - 1) * perPage + 1
  const endItem = Math.min(currentPage * perPage, total)

  if (lastPage <= 1 && !showSizeSelector) {
    return null
  }

  return (
    <div className={`bg-white px-4 py-3 flex items-center justify-between border-t rounded-lg shadow border-gray-200 sm:px-6 ${className}`}>
      <div className='flex-1 flex justify-between sm:hidden'>
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage <= 1 || isLoading}
          className='relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
        >
          Previous
        </button>
        <span className='text-sm text-gray-700 self-center'>
          Page {currentPage} of {lastPage}
        </span>
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage >= lastPage || isLoading}
          className='ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
        >
          Next
        </button>
      </div>
      {isLoading && (
        <span className='text-sm text-gray-500 ml-3'>Loading...</span>
      )}

      <div className='hidden sm:flex-1 sm:flex sm:items-center sm:justify-between'>
        <div className='flex items-center space-x-4'>
          <p className='text-sm text-gray-700'>
            Showing{" "}
            <span className='font-medium'>{total > 0 ? startItem : 0}</span> to{" "}
            <span className='font-medium'>{total > 0 ? endItem : 0}</span> of{" "}
            <span className='font-medium'>{total}</span> results
          </p>

          {showSizeSelector && (
            <div className='flex items-center space-x-2'>
              <label className='text-sm text-gray-700'>Show:</label>
              <select
                value={perPage}
                onChange={(e) => handleSizeChange(Number(e.target.value))}
                disabled={isLoading}
                className='border border-gray-300 rounded-md text-sm px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {sizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className='flex items-center space-x-1'>
          <button
            onClick={() => handlePageChange(1)}
            disabled={currentPage <= 1 || isLoading}
            className='relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-l-md'
            title='First page'
          >
            <ChevronDoubleLeftIcon className='h-4 w-4' />
          </button>

          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage <= 1 || isLoading}
            className='relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
            title='Previous page'
          >
            <ChevronLeftIcon className='h-4 w-4' />
          </button>

          {getVisiblePages().map((page, index) =>
            page === "..." ? (
              <span
                key={`ellipsis-${index}`}
                className='relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700'
              >
                ...
              </span>
            ) : (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                disabled={isLoading}
                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium transition-colors duration-200 ${
                  page === currentPage
                    ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                    : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                }`}
              >
                {page}
              </button>
            )
          )}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= lastPage || isLoading}
            className='relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
            title='Next page'
          >
            <ChevronRightIcon className='h-4 w-4' />
          </button>

          <button
            onClick={() => handlePageChange(lastPage)}
            disabled={currentPage >= lastPage || isLoading}
            className='relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-r-md'
            title='Last page'
          >
            <ChevronDoubleRightIcon className='h-4 w-4' />
          </button>
        </div>
      </div>
    </div>
  )
}

export default memo(Pagination)
